import { ORDER_NUMBER_PATTERN, formatAge } from '@didto/domain';
import type { AgeBucket, ProductionItemStatus, TicketStatus } from '@didto/domain';
import { Badge, Button } from '@didto/ui';
import type { Tone } from '@didto/ui';
import type { ReactNode } from 'react';
import type { ItemLine } from '@/lib/kds';
import { useConnectivity } from '@/lib/connectivity';
import { useOnline } from '@/lib/network';
import type { ChannelStatus } from '@/lib/realtime';
import { ticketTone } from '@/lib/status';
import './live.css';

/**
 * What the Kitchen and Bar display and the Expo board share (Phase 7.5): the connection pill, the
 * order number read from across the room, the age with its word past the thresholds, the state
 * badges with a shape, and the "Action not confirmed" strip. A state is never shown by its colour
 * alone: it always has its word, and a shape that differs from the other states' (ADR-037).
 */

/** One shape per kind of state: hollow waits, half is under way, full is ready, a tick is done. */
export type Mark = 'ring' | 'half' | 'full' | 'check' | 'cross' | 'alert' | 'square';

/** A state in words, with its tone (lib/status.ts) and its shape. */
export function StateBadge({
  tone,
  mark,
  children,
  testId,
}: {
  tone: Tone;
  mark: Mark;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <span
      className={`d-badge${tone === 'neutral' ? '' : ` d-badge--${tone}`} live-state`}
      data-testid={testId}
    >
      <span className={`live-mark live-mark--${mark}`} aria-hidden />
      {children}
    </span>
  );
}

/** A production ticket's state, the same words on the Expo board and on a kitchen card. */
export const TICKET_STATE: Record<TicketStatus, { word: string; mark: Mark }> = {
  queued: { word: 'queued', mark: 'ring' },
  in_progress: { word: 'preparing', mark: 'half' },
  ready: { word: 'READY', mark: 'full' },
  served: { word: 'SERVED', mark: 'check' },
  cancelled: { word: 'cancelled', mark: 'cross' },
};

export function TicketState({ status }: { status: TicketStatus }) {
  const s = TICKET_STATE[status];
  return (
    <StateBadge tone={ticketTone(status)} mark={s.mark}>
      {s.word}
    </StateBadge>
  );
}

/** A production item's state on a kitchen card (its tone is its ticket's: lib/status.ts). */
const ITEM_STATE: Record<ProductionItemStatus, { word: string; mark: Mark; tone: Tone }> = {
  queued: { word: 'queued', mark: 'ring', tone: ticketTone('queued') },
  preparing: { word: 'preparing', mark: 'half', tone: ticketTone('in_progress') },
  ready: { word: 'READY', mark: 'full', tone: ticketTone('ready') },
  cancelled: { word: 'CANCELLED', mark: 'cross', tone: ticketTone('cancelled') },
};

export function ItemState({ status }: { status: ProductionItemStatus }) {
  const s = ITEM_STATE[status];
  return (
    <StateBadge tone={s.tone} mark={s.mark}>
      {s.word}
    </StateBadge>
  );
}

/** The realtime channel in a word: LIVE, or polling while it reconnects (Phase 5 refinement 4). */
export function LivePill({
  status,
  online,
  testId,
}: {
  status: ChannelStatus;
  online: boolean;
  testId: string;
}) {
  const { state, error } = status;
  // the network is up but the server does not answer: said as the banner says it (ADR-037)
  const lost = useConnectivity() === 'lost';
  const [tone, text]: [Tone, string] = !online
    ? ['danger', lost ? 'CONNECTION LOST' : 'OFFLINE']
    : state === 'live'
      ? ['success', 'LIVE']
      : state === 'error'
        ? ['warning', 'RECONNECTING · polling']
        : state === 'connecting'
          ? ['warning', 'CONNECTING · polling']
          : ['neutral', 'POLLING'];
  return (
    <span
      className="live-pill"
      data-testid={testId}
      data-state={online ? state : lost ? 'lost' : 'offline'}
      data-error={error ?? undefined}
      title={error ?? undefined}
    >
      <Badge tone={tone} dot>
        {text}
      </Badge>
    </span>
  );
}

/**
 * The order number as the pass reads it: the day (`MMDD-`) small, the till and the sequence large.
 * The text is the whole number, unchanged.
 */
export function OrderNo({ value }: { value: string | null | undefined }) {
  const m = value ? ORDER_NUMBER_PATTERN.exec(value) : null;
  return (
    <span className="live-no">
      {m ? (
        <>
          <span className="live-no__day">{m[1]}-</span>
          <span className="live-no__main">
            {m[2]}-{m[3]}
          </span>
        </>
      ) : (
        <span className="live-no__main">{value || '…'}</span>
      )}
    </span>
  );
}

const AGE_WORD: Partial<Record<AgeBucket, string>> = { warn: 'WARN', late: 'LATE' };

/**
 * How long it has waited, in large tabular figures; past the warning or late threshold a word
 * stands next to the figures (the figures alone stay `m:ss`).
 */
export function AgeClock({
  seconds,
  bucket,
  testId,
}: {
  seconds: number;
  bucket: AgeBucket;
  testId: string;
}) {
  const word = AGE_WORD[bucket];
  return (
    <div className={`live-age live-age--${bucket}`}>
      <span className="live-age__time" data-testid={testId}>
        {formatAge(seconds)}
      </span>
      {word && (
        <span className="live-age__word">
          <span className="live-mark live-mark--alert" aria-hidden />
          {word}
        </span>
      )}
    </div>
  );
}

/**
 * A command that could not be confirmed stays on its card until it is retried with the same
 * command id or discarded (Phase 5 refinement 1). Retrying needs the server, so it waits for the
 * connection; discarding is local.
 */
export function Unconfirmed({
  label,
  prefix,
  onRetry,
  onDiscard,
}: {
  label: string;
  prefix: 'kds' | 'expo';
  onRetry: () => void;
  onDiscard: () => void;
}) {
  const online = useOnline();
  return (
    <div className="live-unconfirmed" role="alert" data-testid={`${prefix}-unconfirmed`}>
      <span className="live-unconfirmed__text">
        <span className="live-mark live-mark--alert" aria-hidden />
        Action not confirmed — {label}
      </span>
      <div className="live-unconfirmed__actions">
        <Button
          size="lg"
          variant="primary"
          disabled={!online}
          onClick={onRetry}
          data-testid={`${prefix}-retry`}
        >
          Retry
        </Button>
        <Button size="lg" variant="ghost" onClick={onDiscard}>
          Discard
        </Button>
      </div>
    </div>
  );
}

/** A clock time in the location's time zone. */
export function clockTime(iso: string, timeZone: string, seconds = false): string {
  return new Intl.DateTimeFormat('en-PH', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
  }).format(new Date(iso));
}

/** What goes with a line: its variant, its modifiers and its note. */
export function lineMeta(line: ItemLine | null | undefined): string {
  if (!line) return '';
  return [
    line.variant_name_snapshot,
    ...line.order_item_modifier.map((m) => m.option_name_snapshot),
    line.notes ? `“${line.notes}”` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
